import {
  IMultipleRowActions,
  Loading,
  Modal,
  OverflowTooltip,
  Table,
  useFetch,
  useNotificationContext,
} from "@4uhub/lib4uhub";
import ReplayIcon from "@mui/icons-material/Replay";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Backdrop, Chip } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { format } from "date-fns";
import { enUS, es, ptBR } from "date-fns/locale";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { IGerAcessoVisitControlVisitorLog } from "../../../../models/ger-acesso-visit-control-visitor-log";
import { restartAccessControlVisitorSendRetries } from "../../../../services/accessControlVisitorSend.service";
import MainApi from "../../../../services/mainApi.service";
import GerAcessoVisitControlVisitorDetailsModal from "./GerAcessoVisitControlVisitorDetailsModal";
import MacroFilter, { IFilterParams } from "./MacroFilter";

const ACCESS_CONTROL_VISITOR_SEND_ROUTE =
  process.env.REACT_APP_EXTERNAL +
  "/api/v1/AccessControlVisitorSend/AccessControlVisitorSends";

const gerAcessoVisitControlVisitorService =
  new MainApi<IGerAcessoVisitControlVisitorLog>(
    ACCESS_CONTROL_VISITOR_SEND_ROUTE,
  );

const translationPath = "page.4uBus.logs.gerAcessoVisitControlVisitor.";

const GerAcessoVisitControlVisitor = () => {
  const [refresh, setRefresh] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [selectedRow, setSelectedRow] =
    useState<IGerAcessoVisitControlVisitorLog | null>(null);

  const today = useMemo(() => new Date(), []);

  const { setMessage } = useNotificationContext();

  const [filterParams, setFilterParams] = useState<IFilterParams>({
    identifier: "",
    pacientName: "",
    documentCompanion: "",
    statusSend: "",
    CreatedStart: today,
    CreatedEnd: today,
  });

  const {
    t,
    i18n: { language },
  } = useTranslation();

  const { sendRequest: restartAccessControlVisitorSendRetriesReq } = useFetch(
    restartAccessControlVisitorSendRetries,
  );

  const columns: GridColDef<IGerAcessoVisitControlVisitorLog>[] = [
    {
      field: "identifier",
      headerName: t(translationPath + "identifier") || "Identifier",
      flex: 0.7,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
    {
      field: "patientName",
      headerName: t(translationPath + "patient_name") || "Patient Name",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return (
          <OverflowTooltip
            text={params.value}
            tooltip={params.value ?? "-"}
            textTransform="none"
            variant="body2"
          />
        );
      },
    },
    {
      field: "patientEncounterNumber",
      headerName: t(translationPath + "service_number") || "Service Number",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
    {
      field: "visitorDate",
      headerName: t(translationPath + "visit_date") || "Visit Date",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        const visitDate = params.value ?? params.row.patientCompanionDate;
        if (!visitDate) return "-";

        const date = new Date(visitDate);
        return format(date, "Pp", {
          locale:
            language === "pt-BR" ? ptBR : language === "en-US" ? enUS : es,
        }).replaceAll(",", "");
      },
    },
    {
      field: "statusSend",
      headerName: t(translationPath + "status_item") || "Status",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        if (!params.value) return "-";

        const status = params.value.toUpperCase();

        const getStatusConfig = (status: string) => {
          const configs: Record<
            string,
            { color: "warning" | "info" | "success" | "error"; key: string }
          > = {
            P: { color: "warning", key: "pending" },
            PENDENTE: { color: "warning", key: "pending" },
            ENVIANDO: { color: "info", key: "sending" },
            PR: { color: "info", key: "processing" },
            PROCESSANDO: { color: "info", key: "processing" },
            F: { color: "success", key: "finished" },
            FINALIZADO: { color: "success", key: "finished" },
            E: { color: "error", key: "error" },
            ERRO: { color: "error", key: "error" },
          };

          return (
            configs[status] || { color: "warning" as const, key: "pending" }
          );
        };

        const config = getStatusConfig(status);

        return (
          <Chip
            label={t(translationPath + `status.${config.key}`)}
            color={config.color}
            size="small"
          />
        );
      },
    },
    {
      field: "exitDate",
      headerName: t(translationPath + "exit_date") || "Exit Date",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        if (!params.value) return "-";

        const date = new Date(params.value);
        return format(date, "Pp", {
          locale:
            language === "pt-BR" ? ptBR : language === "en-US" ? enUS : es,
        }).replaceAll(",", "");
      },
    },
    {
      field: "visitorIndividualName",
      headerName: t(translationPath + "visitor") || "Visitor",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return params.value ?? params.row.patientCompanionName ?? "-";
      },
    },
    {
      field: "documentCompanion",
      headerName: t(translationPath + "visitor_cpf") || "Visitor CPF",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return params.value ?? "-";
      },
    },
    {
      field: "retries",
      headerName: t(translationPath + "retries") || "Tentativas",
      flex: 0.5,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        return params.value ?? 0;
      },
    },
  ];

  const onRefreshHandler = useCallback(() => {
    setRefresh((prev) => prev + 1);
  }, []);

  const handleFilterChange = useCallback(
    (newFilters: IFilterParams | ((prev: IFilterParams) => IFilterParams)) => {
      setFilterParams(newFilters);
      setRefresh((prev) => prev + 1);
    },
    [],
  );

  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setSelectedRow(null);
  }, []);

  const handleRestartAccessControlVisitorSendRetries = useCallback(
    async (identifier: number) => {
      setIsRetrying(true);

      try {
        const { success } = await restartAccessControlVisitorSendRetriesReq({
          identifier,
        });

        if (success) {
          onRefreshHandler();
          setMessage({
            message: t(`${translationPath}resetedSuccess`),
            type: "success",
          });
          return;
        }

        setMessage({
          message: t(`${translationPath}resyncError`),
          type: "error",
        });
      } catch {
        setMessage({
          message: t(`${translationPath}resyncError`),
          type: "error",
        });
      } finally {
        setIsRetrying(false);
      }
    },
    [onRefreshHandler, restartAccessControlVisitorSendRetriesReq, setMessage, t],
  );

  const handleRestartAccessControlVisitorSendRetriesBatch = useCallback(
    async (identifiers: number[]) => {
      if (identifiers.length === 0) return;

      setIsRetrying(true);

      let failedCount = 0;

      try {
        for (const identifier of identifiers) {
          try {
            const { success } = await restartAccessControlVisitorSendRetriesReq({
              identifier,
            });

            if (!success) {
              failedCount += 1;
            }
          } catch {
            failedCount += 1;
          }
        }

        onRefreshHandler();

        if (failedCount === 0) {
          setMessage({
            message: t(`${translationPath}resetedSuccess`),
            type: "success",
          });
          return;
        }

        setMessage({
          message:
            failedCount === identifiers.length
              ? t(`${translationPath}resyncError`)
              : t(`${translationPath}partialResyncWarning`),
          type: failedCount === identifiers.length ? "error" : "warning",
        });
      } finally {
        setIsRetrying(false);
      }
    },
    [onRefreshHandler, restartAccessControlVisitorSendRetriesReq, setMessage, t],
  );

  const multipleRowsActions = useMemo<IMultipleRowActions[]>(
    () => [
      {
        action: async (ids, api) => {
          const identifiers = Array.from(
            new Set(
              ids
                .map((id) => {
                  const row = api.current.getRow(id) as
                    | IGerAcessoVisitControlVisitorLog
                    | undefined;

                  return Number(row?.identifier ?? id);
                })
                .filter((id) => !Number.isNaN(id) && id > 0),
            ),
          );

          await handleRestartAccessControlVisitorSendRetriesBatch(identifiers);
        },
        icon: <ReplayIcon fontSize="small" />,
        text: t(translationPath + "resync") || "Forcar reenvio",
      },
    ],
    [handleRestartAccessControlVisitorSendRetriesBatch, t],
  );

  return (
    <>
      <Modal open={modalOpen} onClose={handleCloseModal} maxDialogWidth="lg">
        {selectedRow && (
          <GerAcessoVisitControlVisitorDetailsModal
            gerAcessoVisitControlVisitorData={selectedRow}
            onClose={handleCloseModal}
          />
        )}
      </Modal>
      <Backdrop
        open={isRetrying}
        sx={(theme) => ({
          zIndex: theme.zIndex.drawer + 1,
          backgroundColor: "rgba(255, 255, 255, 0.35)",
        })}
      >
        <Loading sx={{ position: "relative" }} size={50} />
      </Backdrop>
      <Table<IGerAcessoVisitControlVisitorLog>
        title={t(translationPath + "title")}
        columns={columns}
        service={gerAcessoVisitControlVisitorService}
        searchInputPlaceHolder={
          t(translationPath + "search_placeholder") || "Search..."
        }
        showDefaultMacroFilters={false}
        showButton={false}
        showDeleteButton={false}
        defaultActions={true}
        multipleRowsActions={multipleRowsActions}
        getRowId={(row) => row.identifier}
        rowActions={(params) => [
          {
            action: () => {
              setSelectedRow(params.row);
              setModalOpen(true);
            },
            icon: <VisibilityIcon fontSize="small" />,
            text: t(translationPath + "view_details") || "Ver detalhes",
          },
          ["ERRO", "E"].includes((params.row.statusSend ?? "").toUpperCase())
            ? {
                action: () => {
                  handleRestartAccessControlVisitorSendRetries(
                    params.row.identifier,
                  );
                },
                icon: <ReplayIcon fontSize="small" />,
                text:
                  t(translationPath + "resync") || "Reiniciar tentativas",
              }
            : null,
        ]}
        serviceProps={useMemo(
          () => ({
            refresh,
            ...filterParams,
            identifier: filterParams.identifier
              ? Number(filterParams.identifier)
              : undefined,
            CreatedStart: filterParams?.CreatedStart
              ? new Date(filterParams.CreatedStart.setHours(15))
                  .toISOString()
                  .substring(0, 10)
              : undefined,
            CreatedEnd: filterParams?.CreatedEnd
              ? new Date(filterParams.CreatedEnd.setHours(15))
                  .toISOString()
                  .substring(0, 10)
              : undefined,
          }),
          [refresh, filterParams],
        )}
        macroFilters={useCallback(
          () => (
            <MacroFilter
              filterParams={filterParams}
              onFilterParams={handleFilterChange}
            />
          ),
          [filterParams, handleFilterChange],
        )}
      />
    </>
  );
};

export default GerAcessoVisitControlVisitor;
